import ipaddr from "ipaddr.js";

/**
 * Source IP allowlist parsing/validation policy, shared by client and server.
 * The portal accepts a single IP or a CIDR range and enforces a least-privilege
 * policy before it is ever used:
 *
 *   - The "any" range (0.0.0.0/0, ::/0) is rejected outright.
 *   - Maximum range size (minimum prefix): IPv4 must be /24 or tighter, IPv6
 *     /48 or tighter. The whole point is narrow allowlists; broad ranges defeat
 *     it, so they are rejected rather than silently accepted.
 *   - Clearly non-public source ranges (loopback, link-local, multicast,
 *     reserved, etc.) are rejected - they can't originate traffic to our infra.
 *   - Private (RFC1918 / unique-local), CGNAT, and broad-but-allowed IPv6
 *     ranges are accepted but flagged with a warning so staff double-check.
 *
 * Values are normalized to their canonical network spec (host bits zeroed).
 * Pure + dependency-light (ipaddr.js only) so it runs in the browser for inline
 * form validation and on the server as the authoritative gate.
 */

/** Smallest allowed IPv4 prefix length (largest allowed range). */
export const IPV4_MIN_PREFIX = 24;
/** Smallest allowed IPv6 prefix length (largest allowed range). */
export const IPV6_MIN_PREFIX = 48;
/** IPv6 prefixes broader than this (a single /64 subnet) get a warning. */
const IPV6_WARN_PREFIX = 64;

// IPv4 range classifications that can't be a public source address.
const IPV4_BLOCKED = new Set([
  "unspecified",
  "broadcast",
  "multicast",
  "linkLocal",
  "loopback",
  "reserved",
  "benchmarking",
  "amt",
  "as112",
]);
// IPv6 range classifications that can't be a public source address.
const IPV6_BLOCKED = new Set([
  "unspecified",
  "multicast",
  "linkLocal",
  "loopback",
  "reserved",
  "benchmarking",
  "amt",
  "ipv4Mapped",
  "rfc6145",
  "rfc6052",
  "6to4",
  "teredo",
  "as112v6",
  "orchid2",
  "droneRemoteIdProtocolEntityTags",
]);

export interface ParsedAllowedIp {
  /** Normalized CIDR network spec, e.g. 203.0.113.0/24 or 2606:4700::/48. */
  cidr: string;
  family: 4 | 6;
  /** Non-blocking advisories (private range, broad IPv6 range). */
  warnings: string[];
}

export type ParseAllowedIpResult =
  | { ok: true; value: ParsedAllowedIp }
  | { ok: false; reason: string };

/**
 * Parse, validate, and normalize an IP / CIDR entry against the allowlist
 * policy. A bare address is treated as a single host (/32 or /128).
 */
export function parseAllowedIp(raw: string): ParseAllowedIpResult {
  const input = raw.trim();
  if (!input) {
    return { ok: false, reason: "Enter an IP address or CIDR range." };
  }

  let addr: ReturnType<typeof ipaddr.parse>;
  let prefix: number;
  try {
    if (input.includes("/")) {
      [addr, prefix] = ipaddr.parseCIDR(input);
    } else {
      addr = ipaddr.parse(input);
      prefix = addr.kind() === "ipv4" ? 32 : 128;
    }
  } catch {
    return {
      ok: false,
      reason: "Enter a valid IP address or CIDR range (e.g. 198.51.100.7 or 203.0.113.0/24).",
    };
  }

  const family: 4 | 6 = addr.kind() === "ipv4" ? 4 : 6;

  if (prefix === 0) {
    return {
      ok: false,
      reason: 'The "any" range (/0) is not allowed. Specify the addresses you connect from.',
    };
  }

  // Normalize to the network address (zero host bits) so it stores as a clean
  // cidr and duplicates collapse (192.168.1.5/24 -> 192.168.1.0/24).
  const cidrInput = `${addr.toString()}/${prefix}`;
  let network: ReturnType<typeof ipaddr.parse>;
  try {
    network =
      family === 4
        ? ipaddr.IPv4.networkAddressFromCIDR(cidrInput)
        : ipaddr.IPv6.networkAddressFromCIDR(cidrInput);
  } catch {
    return { ok: false, reason: "Couldn't parse that network range." };
  }

  const range = network.range();
  if (family === 4 && IPV4_BLOCKED.has(range)) {
    return { ok: false, reason: `That range (${range}) can't be used as a public source address.` };
  }
  if (family === 6 && IPV6_BLOCKED.has(range)) {
    return { ok: false, reason: `That range (${range}) can't be used as a public source address.` };
  }

  const minPrefix = family === 4 ? IPV4_MIN_PREFIX : IPV6_MIN_PREFIX;
  if (prefix < minPrefix) {
    return {
      ok: false,
      reason: `IPv${family} ranges must be /${minPrefix} or smaller (you entered /${prefix}). Narrow the range.`,
    };
  }

  const warnings: string[] = [];
  if (family === 4 && (range === "private" || range === "carrierGradeNat")) {
    warnings.push("private/non-public range");
  }
  if (family === 6 && range === "uniqueLocal") {
    warnings.push("private (unique-local) range");
  }
  if (family === 6 && prefix < IPV6_WARN_PREFIX) {
    warnings.push(`broad range (/${prefix} spans many subnets)`);
  }

  return { ok: true, value: { cidr: `${network.toString()}/${prefix}`, family, warnings } };
}
